import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "state", "runtime", "thump", "action", "pigeon", "api", "meta",
    "bumper", "bumperLabel", "bumperDetail", "bumperProgress", "bumperQueue",
    "bumperResult", "bumperChecked"
  ];
  static values = {
    url: String,
    interval: { type: Number, default: 5000 }
  };

  connect() {
    this.disconnected = false;
    this.load();
    this.startPolling();
  }

  disconnect() {
    this.disconnected = true;
    this.stopPolling();
    if (this.activeRequest) {
      window.clearTimeout(this.activeRequest.timeout);
      this.activeRequest.controller.abort();
      this.activeRequest = null;
    }
  }

  startPolling() {
    this.stopPolling();
    this.timer = window.setInterval(() => this.load(), this.intervalValue);
  }

  stopPolling() {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  async load() {
    if (!this.hasUrlValue || this.disconnected || this.activeRequest) return;

    const request = { controller: new AbortController() };
    this.activeRequest = request;
    request.timeout = window.setTimeout(() => request.controller.abort(), 12_000);

    try {
      const response = await fetch(this.urlValue, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        signal: request.controller.signal
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      if (this.activeRequest !== request || this.disconnected) return;
      if (request.controller.signal.aborted) throw new Error("Monitor request timed out");
      this.render(data);
    } catch (_error) {
      if (this.activeRequest !== request || this.disconnected) return;
      this.element.dataset.state = "down";
      this.stateTarget.textContent = "OFFLINE";
      this.runtimeTarget.textContent = "🕹️ runtime // monitor unavailable";
      this.thumpTarget.textContent = "🥁 next thump // waiting for heartbeat";
      this.actionTarget.textContent = "⚙️ action // could not read thumper state";
      this.pigeonTarget.textContent = "🐦‍⬛ pigeon count // —";
      this.apiTarget.textContent = "🧟 coinzombie.cash api // waiting for next market poll";
      this.metaTarget.textContent = "🐇 watchdog // waiting for next page poll";
      this.bumperUnavailable();
    } finally {
      window.clearTimeout(request.timeout);
      if (this.activeRequest === request) this.activeRequest = null;
    }
  }

  render(data) {
    this.element.dataset.state = data.state || "unknown";
    this.stateTarget.textContent = data.state_label || "CHECK";
    this.runtimeTarget.textContent = data.runtime_text || "🕹️ runtime // checking";
    this.thumpTarget.textContent = data.thump_text || "🥁 next thump // checking";
    this.actionTarget.textContent = data.action_text || "⚙️ action // checking";
    this.pigeonTarget.textContent = data.pigeon_text || "🐦‍⬛ pigeon count // —";
    this.apiTarget.textContent = data.api_text || "🧟 coinzombie.cash api // checking";
    this.metaTarget.textContent = data.meta_text || "🐇 watchdog // armed";
    this.renderBumper(data.bumper_feedback);
  }

  renderBumper(feedback) {
    // Cached pages can still carry the previous Watch markup during a release.
    if (!this.hasBumperTarget) return;

    if (!feedback || typeof feedback !== "object" || Array.isArray(feedback) || Object.keys(feedback).length === 0 ||
        (feedback.reason === "unknown" && !feedback.checked_at)) {
      this.bumperTarget.dataset.bumperState = "unavailable";
      this.bumperTarget.dataset.bumperStale = "false";
      this.setBumperText("bumperLabel", "Awaiting update");
      this.setBumperText("bumperDetail", "Waiting for Bumper’s first update.");
      this.setBumperText("bumperProgress", "Awaiting update");
      this.setBumperText("bumperQueue", "Awaiting update");
      this.setBumperText("bumperResult", "Awaiting update");
      this.setBumperText("bumperChecked", "No update received yet");
      return;
    }

    const labels = {
      working: "Working", waiting: "Waiting", idle: "Ready", paused: "Paused",
      unavailable: "Unavailable", stale: "Update delayed"
    };
    const state = Object.hasOwn(labels, feedback.state) ? feedback.state : "unavailable";
    const stale = state === "stale" || state === "unavailable";
    this.bumperTarget.dataset.bumperState = state;
    this.bumperTarget.dataset.bumperStale = String(stale);
    this.setBumperText("bumperLabel", stale ? labels[state] : this.feedbackText(feedback.label, labels[state]));
    this.setBumperText("bumperDetail", this.feedbackText(feedback.detail, stale
      ? "Current Bumper feedback is unavailable. Last reported figures are shown below."
      : "Waiting for Bumper’s next update."));
    this.setBumperText("bumperProgress", this.feedbackText(feedback.progress_text, "Awaiting update"));
    this.setBumperText("bumperQueue", this.feedbackText(feedback.queue_text, "Awaiting update"));
    this.setBumperText("bumperResult", this.feedbackText(feedback.last_result_text, "Awaiting update"));
    this.setBumperText("bumperChecked", this.checkedText(feedback.checked_at, stale));
  }

  bumperUnavailable() {
    if (!this.hasBumperTarget) return;

    this.bumperTarget.dataset.bumperState = "unavailable";
    this.bumperTarget.dataset.bumperStale = "true";
    this.setBumperText("bumperLabel", "Unavailable");
    this.setBumperText("bumperDetail", "Could not refresh Bumper. Any figures below are from the last update.");
  }

  setBumperText(name, text) {
    const presence = `has${name[0].toUpperCase()}${name.slice(1)}Target`;
    if (!this[presence]) return;

    const target = this[`${name}Target`];
    if (target.textContent !== text) target.textContent = text;
  }

  feedbackText(value, fallback) {
    return typeof value === "string" && value.trim() ? value : fallback;
  }

  checkedText(value, stale) {
    if (typeof value !== "string" || !value.trim()) return "No update received yet";

    const checked = new Date(value);
    if (Number.isNaN(checked.getTime())) return "Update time unavailable";

    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const time = checked.toISOString().slice(11, 19);
    return `${stale ? "Last known update" : "Last update"} · ${months[checked.getUTCMonth()]} ${checked.getUTCDate()}, ${time} UTC`;
  }
}
